import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
    GrikySkillsAdminApiService,
    GrikyAdminSettings,
    SkillThresholds,
} from '../../services/griky-skills-admin-api.service';
import { StorageService } from '../../../../services/storage.service';

interface ThresholdRow {
    key: keyof SkillThresholds;
    from: string;
    to: string;
}

const DEFAULT_THRESHOLDS: SkillThresholds = {
    '1_2': 4,
    '2_3': 12,
    '3_4': 25,
    '4_5': 50,
};

const THRESHOLD_ROWS: ThresholdRow[] = [
    { key: '1_2', from: 'Principiante', to: 'Básico' },
    { key: '2_3', from: 'Básico',       to: 'Intermedio' },
    { key: '3_4', from: 'Intermedio',   to: 'Avanzado' },
    { key: '4_5', from: 'Avanzado',     to: 'Experto' },
];

@Component({
    selector: 'app-gs-settings-page',
    templateUrl: './gs-settings-page.component.html',
    styleUrls: ['./gs-settings-page.component.scss'],
})
export class GsSettingsPageComponent implements OnInit, OnDestroy {
    form: FormGroup;
    loading = true;
    saving = false;
    error: string | null = null;
    successMessage: string | null = null;

    readonly thresholdRows = THRESHOLD_ROWS;

    private companyId = 0;
    private destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private api: GrikySkillsAdminApiService,
        private storage: StorageService,
    ) {
        this.form = this.buildForm(DEFAULT_THRESHOLDS);
    }

    ngOnInit(): void {
        this.companyId = this.storage.get('company')?.id || 0;
        if (!this.companyId) {
            this.error = 'No se pudo obtener la información de la empresa.';
            this.loading = false;
            return;
        }
        this.load();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private buildForm(thresholds: SkillThresholds): FormGroup {
        return this.fb.group({
            thresholds: this.fb.group({
                '1_2': [thresholds['1_2'], [Validators.required, Validators.min(1), Validators.max(500)]],
                '2_3': [thresholds['2_3'], [Validators.required, Validators.min(1), Validators.max(500)]],
                '3_4': [thresholds['3_4'], [Validators.required, Validators.min(1), Validators.max(500)]],
                '4_5': [thresholds['4_5'], [Validators.required, Validators.min(1), Validators.max(500)]],
            }),
        });
    }

    private load(): void {
        this.loading = true;
        this.error = null;
        this.api.getSettings(this.companyId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (settings) => {
                    this.form = this.buildForm(
                        settings.skillThresholds ?? DEFAULT_THRESHOLDS,
                    );
                    this.loading = false;
                },
                error: (err) => {
                    this.error = this.errorMessage(err);
                    this.loading = false;
                },
            });
    }

    save(): void {
        if (this.form.invalid || this.saving) { return; }

        const thresholds = this.form.value.thresholds as SkillThresholds;
        if (!this.hierarchyValid(thresholds)) {
            this.error = 'Las horas deben ser crecientes: 1→2 ≤ 2→3 ≤ 3→4 ≤ 4→5.';
            return;
        }

        this.saving = true;
        this.error = null;
        this.successMessage = null;

        const payload: Partial<GrikyAdminSettings> = {
            skillThresholds: thresholds,
        };

        this.api.updateSettings(this.companyId, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.saving = false;
                    this.successMessage = 'Configuración guardada correctamente.';
                    setTimeout(() => { this.successMessage = null; }, 3500);
                },
                error: (err) => {
                    this.saving = false;
                    this.error = this.errorMessage(err);
                },
            });
    }

    private hierarchyValid(t: SkillThresholds): boolean {
        return t['1_2'] <= t['2_3'] && t['2_3'] <= t['3_4'] && t['3_4'] <= t['4_5'];
    }

    private errorMessage(err: any): string {
        if (err.status === 403) { return 'No tienes permiso para modificar esta configuración.'; }
        if (err.status === 422) {
            const msgs = err.error?.errors
                ? Object.values(err.error.errors).flat().join(', ')
                : err.error?.message;
            return msgs || 'Datos inválidos. Verifica los umbrales.';
        }
        return err.error?.message || 'Error al guardar. Inténtalo de nuevo.';
    }
}
